import { useState } from 'react';
import { Link } from 'react-router-dom';

// Utils
import cn from '@/utils/cn';

// Components
import StrapiMedia from '@/components/atoms/StrapiMedia/StrapiMedia';
import RevealScaleIn from '@/components/reveal-animations/RevealScaleIn/RevealScaleIn';
import RevealVerticalScaleIn from '@/components/reveal-animations/RevealVerticalScaleIn/RevealVerticalScaleIn';
import RevealFadeIn from '@/components/reveal-animations/RevealFadeIn/RevealFadeIn';
import RevealFadeInDown from '@/components/reveal-animations/RevealFadeInDown/RevealFadeInDown';
import MixedTitle from '@/components/molecules/MixedTitle/MixedTitle';
import Button from '@/components/atoms/Button/Button';
import Paragraph from '@/components/atoms/Paragraph/Paragraph';

// Types
import { HeroSectionType } from '@/components/blocks/HeroSection/HeroSectionType';
import { GradientType } from '@/types/GradientType';
import { getGradientByColor } from '@/utils/color';

// Styles
import { cssLinearGradientStopsToSvgObjectBoundingBox } from '@/utils/cssLinearGradientSvg';
import { WaveShapeEnum } from '@/enum/WaveShapeEnum';
import styles from './HeroSection.module.css';

// Utils
import { getHeroBackgroundSvgConfig } from './heroSectionBackgroundShapes';

const truncateText = (text: string, maxLength: number = 40): string =>
  text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

const HERO_GRADIENT_ANGLE_DEG = 103.39;
const HERO_GRADIENT_STOP_START_PCT = -0.12;
const HERO_GRADIENT_STOP_END_PCT = 110.86;

interface HeroSectionProps {
  title: HeroSectionType['title'];
  paragraph: HeroSectionType['paragraph'];
  cta: HeroSectionType['cta'];
  shape: HeroSectionType['shape'];
  sideCTA: HeroSectionType['sideCTA'];
  color: HeroSectionType['color'];
  gradient: HeroSectionType['gradient'];
  scale: HeroSectionType['scale'];
  media: HeroSectionType['media'];
  customContent?: React.ReactNode;
  className?: string;
}

function HeroSection({
  title,
  paragraph,
  cta = null,
  shape,
  sideCTA = null,
  color,
  gradient,
  scale,
  media = null,
  customContent = null,
  className,
}: HeroSectionProps) {
  const isHeadDisplayed = media?.url;
  const backgroundShapeType = shape;
  const backgroundSvg = getHeroBackgroundSvgConfig(shape);

  const [gradientTheme] = useState<GradientType>(() => {
    if (gradient) {
      return gradient;
    }

    const theme = getGradientByColor(color);

    if (theme) {
      return theme;
    }

    return { start: 'var(--gradient-dark-blue-start)', end: 'var(--gradient-dark-blue-end)' };
  });

  const gradientLine = cssLinearGradientStopsToSvgObjectBoundingBox(
    HERO_GRADIENT_ANGLE_DEG,
    HERO_GRADIENT_STOP_START_PCT,
    HERO_GRADIENT_STOP_END_PCT,
  );

  return (
    <div className={cn([styles.hero, className, shape === WaveShapeEnum.CONCAVE && styles.heroConcave])}>
      <div className="section-marker" data-theme="dark" />
      <RevealVerticalScaleIn className={cn([styles.background, styles[scale]])}>
        <svg
          className={styles.backgroundSvg}
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="100%"
          preserveAspectRatio="none"
          viewBox="0 0 1511.998 864"
        >
          <defs>
            <linearGradient
              id="heroSectionGradient"
              x1={gradientLine.x1}
              y1={gradientLine.y1}
              x2={gradientLine.x2}
              y2={gradientLine.y2}
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor={gradientTheme.start} />
              <stop offset="100%" stopColor={gradientTheme.end} />
            </linearGradient>
          </defs>
          <path d={backgroundSvg.d} fill="url(#heroSectionGradient)" transform="">
            <animate
              attributeName="d"
              repeatCount="indefinite"
              keyTimes="0; 0.5; 1"
              values={backgroundSvg.animateValues}
              begin="0s"
              dur={`${backgroundSvg.dur}s`}
            />
          </path>
        </svg>
      </RevealVerticalScaleIn>

      <div
        className={cn([
          styles.wrapper,
          isHeadDisplayed ? styles.twoColumns : '',
          customContent ? styles.withCustomContent : '',
          sideCTA ? styles.withSideLink : '',
        ])}
      >
        {isHeadDisplayed && (
          <RevealFadeInDown className={styles.head} delay={0.7}>
            <StrapiMedia image={media} format="xlarge" className={styles.headImg} />
          </RevealFadeInDown>
        )}

        {customContent || (
          <div className={cn([styles.content, styles[backgroundShapeType], !isHeadDisplayed && styles.withoutHead])}>
            {sideCTA && (
              <Link className={cn([styles.sideLink, 'font-uppercase'])} to={sideCTA.link}>
                <RevealScaleIn delay={0.8} className={styles.sideLinkLabel}>
                  {truncateText(sideCTA.label)}
                </RevealScaleIn>
              </Link>
            )}
            <RevealFadeIn delay={0.4}>
              <MixedTitle
                className={cn([styles.title, 'text-center'])}
                title={title.title}
                seoTitleTag={title.seoTitleTag}
                color={title.color}
                colorSpan={title.colorSpan}
                size={title.size}
                sizeSpan={title.sizeSpan}
              />
            </RevealFadeIn>

            {paragraph && (
              <RevealFadeIn className={styles.text} delay={0.6}>
                <Paragraph text={paragraph.text} color={paragraph.color} size={paragraph.size} />
              </RevealFadeIn>
            )}
            {cta ? (
              <RevealFadeIn className={styles.action} delay={0.8}>
                <Link to={cta.link}>
                  <Button color={cta.color}>{cta.label}</Button>
                </Link>
              </RevealFadeIn>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

export default HeroSection;
